import { toast } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { npcName } from '../i18n/labels';
import { say as speak, sayEnglish, VOICES, type Voice } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { remember } from './journal';
import { dayNumber, logOrder } from './passport';

/** Dialog helpers shared by the classroom cases and the school events: who speaks, in which voice. */
export type Who = 'xiaohua' | 'xiaomei' | 'azhe' | 'xiaoqiang' | 'xiaofang' | 'teacher' | 'nurse' | 'bo' | 'mom' | 'narrator' | 'me';

export const ICON: Record<Who, string> = {
  xiaohua: '😅', xiaomei: '📚', azhe: '😜', xiaoqiang: '⚽', xiaofang: '🎀',
  teacher: '👩‍🏫', nurse: '🩺', bo: '👦', mom: '👩', narrator: '📖', me: '🙂',
};

export const VOICE_OF: Record<Who, Voice | undefined> = {
  xiaohua: VOICES.xiaohua, xiaomei: VOICES.xiaomei, azhe: VOICES.azhe, xiaoqiang: VOICES.xiaoqiang, xiaofang: VOICES.xiaofang,
  teacher: VOICES.teacher, nurse: VOICES.nurse, bo: VOICES.kid, mom: VOICES.mom, narrator: undefined, me: undefined,
};

/** Set by `phrase()` so the next line doesn't cut off the English phrase being read aloud. */
let hush = false;

/** Show (and speak) one line. The narrator is never voiced. */
export function line(who: Who, text: string, choices?: Choice[], icon?: string, name?: string): void {
  if (hush) hush = false;
  else if (VOICE_OF[who]) speak(text, VOICE_OF[who]);
  const title = name ?? (who === 'narrator' ? m().classroom.ui.narrator : who === 'me' ? m().common.me : npcName(who));
  say({ who: title, icon: icon ?? ICON[who], text, choices });
}

/** A key English phrase: shown in bold, read aloud in English, and counted as a passport "talk". */
export function phrase(who: Who, intro: string, text: string, next: () => void, icon?: string): void {
  line(who, intro ? `${intro}\n**“${text}”**` : `**“${text}”**`, [
    {
      text: m().classroom.ui.sayIt,
      run: () => {
        sayEnglish(text);
        logOrder();
        toast(m().classroom.ui.practised);
        hush = true;
        next();
        hush = false;
      },
    },
  ], icon);
}

/**
 * Record an outcome in an NPC's memory, replacing the other outcomes of the same
 * story, so reactions always follow the latest thing that happened.
 */
export function setOutcome(npc: string, flag: string, group: readonly string[]): void {
  const mem = S().memory;
  mem[npc] = (mem[npc] ?? []).filter((f) => !group.includes(f));
  note(npc, flag);
}

/** `remember()` plus the day it happened, so thanks and news wait until a later day. */
export function note(npc: string, flag: string): void {
  remember(npc, flag);
  S().npcTalk[`cls.mem.${npc}`] = dayNumber();
}

export function forget(npc: string, flag: string): void {
  const mem = S().memory;
  if (mem[npc]) mem[npc] = mem[npc].filter((f) => f !== flag);
}

export const shuffled = <T,>(a: readonly T[]): T[] => a.map((v) => [Math.random(), v] as const).sort((x, y) => x[0] - y[0]).map((p) => p[1]);
